import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';

export interface PassengerOnboardingLookupResult {
  foundInSystem: boolean;
  foundInHotspot: boolean;
  canPrefill: boolean;
  canSendEmailVerification: boolean;
  existingUser?: any;
  prefill?: {
    passengerProfileID?: number | string | null;
    hotspotPassengerID?: number | string | null;
    hotspotProvider?: string | null;
    displayName?: string | null;
    fullName?: string | null;
    userName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
    emailAddress?: string | null;
  };
}

export interface PassengerOnboardingStartResult extends PassengerOnboardingLookupResult {
  userCreated: boolean;
  emailVerificationQueued: boolean;
  notificationDeliveryID?: number | string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PassengerOnboardingService {
  private apiUrl = `${environment.apiUrl}/passenger-onboarding`;

  constructor(private http: HttpClient) {}

  lookup(contact: string): Observable<PassengerOnboardingLookupResult> {
    const params = new HttpParams().set('contact', contact.trim());
    return this.http.get<any>(`${this.apiUrl}/lookup`, { params }).pipe(
      map(response => this.normalizeLookupResult(response))
    );
  }

  start(contact: string, verificationUrlBase: string, email?: string): Observable<PassengerOnboardingStartResult> {
    const payload: { contact: string; verificationUrlBase: string; email?: string } = {
      contact: contact.trim(),
      verificationUrlBase
    };

    if (email?.trim()) {
      payload.email = email.trim();
    }

    return this.http.post<any>(`${this.apiUrl}/start`, payload).pipe(
      map(response => this.normalizeStartResult(response))
    );
  }

  private normalizeLookupResult(response: any): PassengerOnboardingLookupResult {
    return {
      foundInSystem: Boolean(response.foundInSystem ?? response.FoundInSystem),
      foundInHotspot: Boolean(response.foundInHotspot ?? response.FoundInHotspot),
      canPrefill: Boolean(response.canPrefill ?? response.CanPrefill),
      canSendEmailVerification: Boolean(response.canSendEmailVerification ?? response.CanSendEmailVerification),
      existingUser: response.existingUser ?? response.ExistingUser,
      prefill: this.normalizePrefill(response.prefill ?? response.Prefill)
    };
  }

  private normalizeStartResult(response: any): PassengerOnboardingStartResult {
    const lookup = this.normalizeLookupResult(response);

    return {
      ...lookup,
      userCreated: Boolean(response.userCreated ?? response.UserCreated),
      emailVerificationQueued: Boolean(response.emailVerificationQueued ?? response.EmailVerificationQueued),
      notificationDeliveryID: response.notificationDeliveryID ?? response.NotificationDeliveryID
    };
  }

  private normalizePrefill(prefill: any): PassengerOnboardingLookupResult['prefill'] {
    if (!prefill) {
      return undefined;
    }

    return {
      passengerProfileID: prefill.passengerProfileID ?? prefill.PassengerProfileID,
      hotspotPassengerID: prefill.hotspotPassengerID ?? prefill.HotspotPassengerID,
      hotspotProvider: prefill.hotspotProvider ?? prefill.HotspotProvider,
      displayName: prefill.displayName ?? prefill.DisplayName,
      fullName: prefill.fullName ?? prefill.FullName,
      userName: prefill.userName ?? prefill.UserName,
      firstName: prefill.firstName ?? prefill.FirstName,
      lastName: prefill.lastName ?? prefill.LastName,
      phone: prefill.phone ?? prefill.Phone,
      phoneNumber: prefill.phoneNumber ?? prefill.PhoneNumber,
      email: prefill.email ?? prefill.Email,
      emailAddress: prefill.emailAddress ?? prefill.EmailAddress
    };
  }
}
